import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";
import Dropzone from "dropzone";
import {
  findElement,
  getMetaValue,
  insertAfter,
  removeElement,
} from "../helpers/dropzone";

// Connects to data-controller="dropzone"
export default class extends Controller {
  static targets = ["input"];

  connect() {
    // console.log("Connected to the dropzone controller");
    this.dropZone = createDropZone(this);
    this.hideFileInput();
    this.bindEvents();
    Dropzone.autoDiscover = false; // necessary quirk for Dropzone error in console
    // If direct uploads are disabled via data-direct-upload="false",
    // intercept the form submit and send files with the form (multipart/form-data)
    if (!this.directUploadEnabled) this.setupNonDirectUpload();
  }

  hideFileInput() {
    this.inputTarget.style.display = "none";
    this.inputTarget.disabled = true;
  }

  bindEvents() {
    this.dropZone.on("addedfile", (file) => {
      setTimeout(() => {
        if (this.directUploadEnabled) {
          file.accepted && createDirectUploadController(this, file).start();
        }
      }, 500);
    });

    this.dropZone.on("removedfile", (file) => {
      file.controller && removeElement(file.controller.hiddenInput);
    });
    this.dropZone.on("canceled", (file) => {
      file.controller && file.controller.xhr.abort();
    });
    this.dropZone.on("processing", (file) => {
      this.submitButton.disabled = true;
    });
    this.dropZone.on("queuecomplete", (file) => {
      this.submitButton.disabled = false;
    });
  }

  get headers() {
    return { "X-CSRF-Token": getMetaValue("csrf-token") };
  }
  get url() {
    return this.inputTarget.getAttribute("data-direct-upload-url");
  }
  get maxFiles() {
    return this.data.get("maxFiles") || 1;
  }
  get maxFileSize() {
    return this.data.get("maxFileSize") || 256;
  }
  get acceptedFiles() {
    return this.data.get("acceptedFiles");
  }

  get addRemoveLinks() {
    return this.data.get("addRemoveLinks") || true;
  }
  get uploadMultiple() {
    return this.data.get("uploadMultiple") || false;
  }
  get form() {
    return this.element.closest("form");
  }
  get directUploadEnabled() {
    // default enabled; set `data-direct-upload="false"` to disable
    return this.data.get("directUpload") !== "false";
  }

  setupNonDirectUpload() {
    if (!this.form) return;
    this.form.addEventListener("submit", (e) => this.handleFormSubmit(e));
  }

  async handleFormSubmit(event) {
    event.preventDefault();
    const submitBtn = this.submitButton;
    if (submitBtn) submitBtn.disabled = true;

    const form = this.form;
    const formData = new FormData(form);

    const fileInputName = this.inputTarget.name;
    if (this.dropZone && this.dropZone.files && this.dropZone.files.length) {
      this.dropZone.files.forEach((file) =>
        formData.append(fileInputName, file),
      );
    }

    try {
      const response = await fetch(form.action, {
        method: (form.method || "POST").toUpperCase(),
        body: formData,
        credentials: "same-origin",
      });

      if (response.redirected) {
        window.location.href = response.url;
        return;
      }

      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const json = await response.json();
        if (json && json.redirect_to) {
          window.location.href = json.redirect_to;
          return;
        }
      }
      window.location.reload();
    } catch (err) {
      console.error("Error submitting form with files:", err);
      if (submitBtn) submitBtn.disabled = false;
      alert("Erro ao enviar arquivos. Confira o console para mais detalhes.");
    }
  }
  get submitButton() {
    return findElement(this.form, "input[type=submit], button[type=submit]");
  }
}
class DirectUploadController {
  constructor(source, file) {
    this.directUpload = createDirectUpload(file, source.url, this);
    this.source = source;
    this.file = file;
  }
  start() {
    this.file.controller = this;
    this.hiddenInput = this.createHiddenInput();
    this.directUpload.create((error, attributes) => {
      if (error) {
        removeElement(this.hiddenInput);
        this.emitDropzoneError(error);
      } else {
        this.hiddenInput.value = attributes.signed_id;
        this.emitDropzoneSuccess();
      }
    });
  }
  createHiddenInput() {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = this.source.inputTarget.name;
    insertAfter(input, this.source.inputTarget);
    return input;
  }
  directUploadWillStoreFileWithXHR(xhr) {
    // Don't send credentials to the storage service; that forces stricter
    // CORS handling on the S3 side. ActiveStorage will set required headers
    // like Content-Type; avoid adding app-specific credentials here.
    try {
      xhr.withCredentials = false;
    } catch (e) {}
    this.bindProgressEvent(xhr);
    this.emitDropzoneUploading();
  }
  bindProgressEvent(xhr) {
    this.xhr = xhr;
    this.xhr.upload.addEventListener("progress", (event) =>
      this.uploadRequestDidProgress(event),
    );
  }
  uploadRequestDidProgress(event) {
    const element = this.source.element;
    const progress = (event.loaded / event.total) * 100;
    findElement(this.file.previewTemplate, ".dz-upload").style.width =
      `${progress}%`;
  }

  emitDropzoneUploading() {
    this.file.status = Dropzone.UPLOADING;
    this.source.dropZone.emit("processing", this.file);
  }

  emitDropzoneError(error) {
    this.file.status = Dropzone.ERROR;
    this.source.dropZone.emit("error", this.file, error);
    this.source.dropZone.emit("complete", this.file);
  }
  emitDropzoneSuccess() {
    this.file.status = Dropzone.SUCCESS;
    this.source.dropZone.emit("success", this.file);
    this.source.dropZone.emit("complete", this.file);
  }
}

function createDirectUploadController(source, file) {
  return new DirectUploadController(source, file);
}

function createDirectUpload(file, url, controller) {
  return new DirectUpload(file, url, controller);
}

function createDropZone(controller) {
  let dropzone = new Dropzone(controller.element, {
    url: controller.directUploadEnabled
      ? controller.url
      : controller.form
        ? controller.form.action
        : controller.url,
    // Avoid sending app-specific headers (e.g. X-CSRF-Token) on direct
    // uploads to the storage service — these can trigger CORS preflight
    // failures when the storage bucket isn't configured to allow them.
    maxFiles: controller.maxFiles,
    maxFilesize: controller.maxFileSize,
    acceptedFiles: controller.acceptedFiles,
    addRemoveLinks: controller.addRemoveLinks,
    uploadMultiple: controller.uploadMultiple,
    autoQueue: false,
  });
  // console.log(dropzone);
  return dropzone;
}
